import { RefObject, useEffect } from 'react';

// The host supplies a stable viewport unit: iframe vh would otherwise grow
// with the reported content height and create a resize feedback loop.
export function useUniverseEmbed(scene: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (window.parent === window || !scene.current) return;
    const root = document.documentElement;
    const element = scene.current;
    let parentOrigin: string | undefined;
    let lastHeight = 0;
    const report = () => {
      if (!parentOrigin) return;
      const height = Math.ceil(element.offsetHeight + element.offsetTop);
      if (height === lastHeight) return;
      lastHeight = height;
      window.parent.postMessage({ type: 'overture-universe:height', height }, parentOrigin);
    };
    const receive = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      let origin: URL;
      try { origin = new URL(event.origin); } catch { return; }
      if (!['https://overtureai.org', 'https://www.overtureai.org'].includes(event.origin)
        && !(['http:', 'https:'].includes(origin.protocol) && ['localhost', '127.0.0.1'].includes(origin.hostname))) return;
      if (event.data?.type !== 'overture-universe:viewport' || !Number.isFinite(event.data.height) || event.data.height <= 0) return;
      parentOrigin = event.origin;
      root.style.setProperty('--universe-vh', `${event.data.height / 100}px`);
      root.classList.add('universe-embedded');
      report();
    };
    const observer = new ResizeObserver(report);
    observer.observe(element);
    window.addEventListener('message', receive);
    // No private data; the host replies with its validated viewport dimensions.
    window.parent.postMessage({ type: 'overture-universe:ready' }, '*');
    return () => {
      observer.disconnect();
      window.removeEventListener('message', receive);
      root.style.removeProperty('--universe-vh');
      root.classList.remove('universe-embedded');
    };
  }, [scene]);
}
